import { useEffect, useMemo, useRef, useState } from "react";
import tw from "tailwind-styled-components";
import {
    MdClose,
    MdAdd,
    MdAccessTime,
    MdDeleteOutline,
    MdSearch,
    MdRepeat,
    MdCalendarToday,
    MdCalendarMonth,
} from "react-icons/md";
import { FaRegCalendarAlt } from "react-icons/fa";
import { useMutation } from "react-query";
import axios from "axios";
import { toast } from "react-toastify";
import moment from "moment";
import { useSelector } from "react-redux";
import {
    ModalHeader,
    Wrapper,
    ScrollableContent,
    SideCancelBtn,
    SubmitBtn,
    DeleteBtn,
} from "../../Components/Styles/InputStyles";
import AddEditScheduledReport, { INITIAL_VALUES } from "../../Components/Reports/AddEditScheduledReport";
import Config from "../../Config";
import SideModal from "../../Components/SideModal";
import Model from "../../Components/Model";
import Loading from "../../Components/Loading";
import { Button } from "../../Components/Styles/PageStyles";

const REPORT_TYPES = [
    { reportType: 3, title: "Tenant" },
    { reportType: 5, title: "Room Occupancy" },
    { reportType: 6, title: "Bed Occupancy" },
    { reportType: 1, title: "Collections" },
    { reportType: 2, title: "Dues" },
    { reportType: 4, title: "Expenses" },
    { reportType: 7, title: "Complaints" },
    { reportType: 8, title: "Leads" },
    { reportType: 11, title: "Booking" },
    { reportType: 12, title: "Property" },
    { reportType: 9, title: "Activity Log" },
    { reportType: 10, title: "Moved Out Tenants" },
    { reportType: 13, title: "Property Lease" },
    { reportType: 14, title: "Refund Request" },
    { reportType: 15, title: "Rent Review" },
    { reportType: 16, title: "Profit & Loss" },
    { reportType: 18, title: "Housekeeping Report" },
];

const FREQUENCY_OPTIONS = [
    { value: 1, label: "Daily" },
    { value: 2, label: "Weekly" },
    { value: 3, label: "Monthly" },
];

export const REPORT_SCHEDULE_STATUS = {
    ACTIVE: 1,
    PAUSE: 2,
    DELETED: 3,
};

const makeInitialValues = () => ({
    ...INITIAL_VALUES,
    emails: [...(INITIAL_VALUES.emails || [])],
    properties: [...(INITIAL_VALUES.properties || [])],
});

const to24HourWithSeconds = (timeStr) => {
    if (!timeStr) return null;

    const formats = [
        "h:mm A", "hh:mm A", "h:mm:ss A", "hh:mm:ss A",
        "HH:mm", "HH:mm:ss", "H:mm", "H:mm:ss",
    ];
    const parsed = moment(String(timeStr).trim(), formats, true);
    if (!parsed.isValid()) return null;
    return parsed.format("HH:mm:ss");
};

// const getReportTitle = (type) =>
//     REPORT_TYPES.find((item) => item.reportType === Number(type))?.title ?? "-";

const getFrequencyLabel = (frequency) =>
    FREQUENCY_OPTIONS.find((item) => item.value === Number(frequency))?.label ?? "-";

const getEmails = (schedule) => {
    if (schedule?.email != null) {
        return String(schedule.email).split(",").map((email) => email.trim()).filter(Boolean);
    }
    return Array.isArray(schedule?.emails) ? schedule.emails : [];
};

const getPropertyIds = (schedule) => {
    if (schedule?.propId != null) {
        return String(schedule.propId)
            .split(",")
            .map((id) => Number(id.trim()))
            .filter((id) => Number.isInteger(id) && id > 0);
    }
    return (Array.isArray(schedule?.propertyIds) ? schedule.propertyIds : [])
        .map(Number)
        .filter((id) => Number.isInteger(id) && id > 0);
};

const formatDisplayTime = (value) => {
    if (!value) return "-";
    const parsed = moment(String(value).trim(), [
        "HH:mm:ss", "HH:mm", "H:mm:ss", "H:mm",
        "h:mm A", "hh:mm A", "h:mm:ss A", "hh:mm:ss A",
    ], true);
    return parsed.isValid() ? parsed.format("hh:mm A") : String(value);
};

const getErrorMessage = (error, fallback = "Something went wrong. Please try again.") =>
    error?.response?.data?.message ||
    error?.response?.data?.msg ||
    error?.message ||
    fallback;

const ScheduleReport = ({ handleClose, setShowScheduleReport, showScheduleReport, tenantAttendanceEnabled }) => {
    const formikRef = useRef(null);
    const user = useSelector((state) => state.UserReducer.user);
    const propertyList = useSelector((state) => state.PropertyReducer.property);
    const reportTypes = [
        ...REPORT_TYPES,
        ...(Number(tenantAttendanceEnabled) === 1
            ? [{ reportType: 17, title: "Tenant Attendance" }]
            : []),
    ];
     const getReportTitle = (type) =>
    reportTypes.find(
      (item) => item.reportType === Number(type)
    )?.title ?? "-";
    const [apiData, setApiData] = useState([]);

    const filteredProperties = useMemo(
        () => (Array.isArray(propertyList) ? propertyList : []).filter(
            (property) => Number(property.id) !== 0
        ),
        [propertyList]
    );

    const [searchText, setSearchText] = useState("");
    const [editingScheduleId, setEditingScheduleId] = useState(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [formInitialValues, setFormInitialValues] = useState(makeInitialValues);
    const [confirmAction, setConfirmAction] = useState(null);
    const [viewPropertiesAction, setViewPropertiesAction] = useState(null);

    const fetchScheduledList = async () => {
        const response = await axios.get(`${Config.apiUrl}/report/schedule/list`, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            },
        });
        return response.data;
    };

    const {
        isLoading: isScheduleListLoading,
        mutate: getScheduleListMutate,
    } = useMutation(fetchScheduledList, {
        onMutate: () => {
            setShowScheduleReport(true);
        },
        onSuccess: (res) => {
            setApiData(
                res?.isSuccess === false
                    ? []
                    : res?.data || []
            );
        },
        onError: () => {
            setApiData([]);
            toast.error("Failed to fetch scheduled reports.");
        },
    });

    useEffect(() => {
        if (showScheduleReport) {
            getScheduleListMutate();
        }
    }, [showScheduleReport]);

    // Helper to map ID array to property names
    const getSelectedPropertyNames = (schedule) => {
        const ids = getPropertyIds(schedule);
        if (!ids.length) return [];

        return ids.map(id => {
            const match = propertyList?.find(p => Number(p.id) === id);
            return match ? match.name || match.title || match.propertyName : `Property ${id}`;
        });
    };

    // Add schedule
    const addScheduleFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/report/schedule`, values, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                "Content-Type": "application/json",
            },
        });
    };

    const onAddScheduleSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Schedule added successfully");
            getScheduleListMutate();
            resetForm();
        } else {
            toast.error(data?.data?.msg || "Failed to add schedule");
        }
    };

    const onAddScheduleError = (error) => {
        toast.error(getErrorMessage(error));
    };

    const { isLoading: addScheduleLoading, mutate: addScheduleMutate } = useMutation(
        addScheduleFunction,
        { onSuccess: onAddScheduleSuccess, onError: onAddScheduleError }
    );

    // Edit schedule
    const editScheduleFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/report/schedule`, values, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                "Content-Type": "application/json",
            },
        });
    };

    const onEditScheduleSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Schedule updated successfully");
            getScheduleListMutate();
            resetForm();
        } else {
            toast.error(data?.data?.msg || "Failed to update schedule");
        }
    };

    const onEditScheduleError = (error) => {
        toast.error(getErrorMessage(error));
    };

    const { isLoading: editScheduleLoading, mutate: editScheduleMutate } = useMutation(
        editScheduleFunction,
        { onSuccess: onEditScheduleSuccess, onError: onEditScheduleError }
    );

    // Pause / resume / delete schedule
    const updateScheduleStatusFunction = async (values) => {
        return await axios.post(
            `${Config.apiUrl}/report/schedule/update/status`,
            values,
            {
                headers: {
                    authorization: "Bearer" + " " + user.token,
                    "Content-Type": "application/json",
                },
            }
        );
    };

    const onUpdateScheduleStatusSuccess = (data, variables) => {
        if (data?.data?.isSuccess === true) {
            const statusMessages = {
                [REPORT_SCHEDULE_STATUS.ACTIVE]: "Report schedule resumed. Reports will be sent as per the schedule.",
                [REPORT_SCHEDULE_STATUS.PAUSE]: "Report schedule paused. No reports will be sent until you resume it.",
                [REPORT_SCHEDULE_STATUS.DELETED]: "Report schedule deleted successfully.",
            };
            toast.success(data?.data?.msg || statusMessages[variables.status] || "Schedule updated successfully");
            if (editingScheduleId === variables.id) resetForm();
            setConfirmAction(null);
            getScheduleListMutate();
        } else {
            toast.error(data?.data?.msg || "Failed to update schedule");
        }
    };

    const onUpdateScheduleStatusError = (error) => {
        toast.error(getErrorMessage(error));
    };

    const {
        isLoading: updateScheduleStatusLoading,
        mutate: updateScheduleStatusMutate,
    } = useMutation(updateScheduleStatusFunction, {
        onSuccess: onUpdateScheduleStatusSuccess,
        onError: onUpdateScheduleStatusError,
    });

    const isSaving = addScheduleLoading || editScheduleLoading;
    const isStatusSaving = updateScheduleStatusLoading;

    const resetForm = () => {
        setFormInitialValues(makeInitialValues());
        setEditingScheduleId(null);
        setIsAddingNew(false);
    };

    const handleAddNew = () => {
        setFormInitialValues(makeInitialValues());
        setEditingScheduleId(null);
        setIsAddingNew(true);
    };

    const handleEdit = (schedule) => {
        setFormInitialValues({
            ...makeInitialValues(),
            reportType: schedule.reportType == null ? null : Number(schedule.reportType),
            reportTypeName: schedule.reportTypeName ?? "",
            frequency: schedule.frequency == null ? "" : Number(schedule.frequency),
            time: schedule.sendingTime ?? schedule.time ?? "",
            emails: getEmails(schedule),
            properties: getPropertyIds(schedule),
        });
        setEditingScheduleId(schedule.id);
        setIsAddingNew(false);
    };

    const openConfirmation = (type, schedule) => {
        setConfirmAction({ type, schedule });
    };

    const closeConfirmation = () => {
        if (isStatusSaving) return;
        setConfirmAction(null);
    };

    const confirmAndSubmit = () => {
        if (!confirmAction || isStatusSaving) return;
        if (!user?.token) {
            toast.error("Your session has expired. Please log in again.");
            return;
        }

        const { type, schedule } = confirmAction;
        const status = type === "delete"
            ? REPORT_SCHEDULE_STATUS.DELETED
            : Number(schedule.status) === REPORT_SCHEDULE_STATUS.ACTIVE
                ? REPORT_SCHEDULE_STATUS.PAUSE
                : REPORT_SCHEDULE_STATUS.ACTIVE;

        updateScheduleStatusMutate({ id: schedule.id, status });
    };

    const handleSubmit = (values) => {
        if (isSaving) return;
        if (!user?.token) {
            toast.error("Your session has expired. Please log in again.");
            return;
        }

        const sendingTime = to24HourWithSeconds(values.time);
        if (!sendingTime) {
            toast.error("Please select a valid sending time.");
            return;
        }

        const payload = {
            reportType: Number(values.reportType),
            reportTypeName: values.reportTypeName,
            frequency: Number(values.frequency),
            sendingTime,
            propId: (values.properties || []).join(","),
            email: (values.emails || []).map((email) => String(email).trim()).filter(Boolean).join(","),
        };

        if (editingScheduleId != null) {
            editScheduleMutate({ ...payload, id: editingScheduleId });
        } else {
            addScheduleMutate(payload);
        }
    };

    const availableSchedules = (Array.isArray(apiData) ? apiData : []).filter(
        (schedule) => Number(schedule.status) !== REPORT_SCHEDULE_STATUS.DELETED
    );
    // const search = searchText.trim().toLowerCase();
    // const filteredSchedules = availableSchedules.filter((schedule) =>
    //     getReportTitle(schedule.reportType).toLowerCase().includes(search)
    // );
    const search = searchText.trim().toLowerCase();
    const filteredSchedules = availableSchedules.filter((schedule) => {
        const reportType = getReportTitle(schedule.reportType).toLowerCase();
        const scheduledByName = String(schedule.scheduledByName || "").toLowerCase();

        return (
            reportType.includes(search) ||
            scheduledByName.includes(search)
        );
    });

    const isEditing = editingScheduleId != null;
    const showForm = isAddingNew || isEditing;
    const isDeleteAction = confirmAction?.type === "delete";
    const isCurrentlyPaused = Number(confirmAction?.schedule?.status) === REPORT_SCHEDULE_STATUS.PAUSE;
    const confirmTitle = isDeleteAction
        ? "Confirm Deletion"
        : isCurrentlyPaused ? "Confirm Resume" : "Confirm Pause";
    const confirmMessage = isDeleteAction
        ? `Are you sure you want to delete this scheduled report?`
        : isCurrentlyPaused
            ? "Are you sure you want to resume this scheduled report?"
            : "Are you sure you want to pause this scheduled report?";
    const confirmButtonText = isStatusSaving
        ? isDeleteAction ? "Deleting..." : isCurrentlyPaused ? "Resuming..." : "Pausing..."
        : isDeleteAction ? "Delete" : isCurrentlyPaused ? "Resume" : "Pause";

    return (
        <>
            <Container>
                <ModalHeader>
                    <h1 className="text-white text-lg font-medium">Schedule Report</h1>
                    <button type="button" onClick={handleClose} aria-label="Close schedule reports">
                        <MdClose color="white" size={24} />
                    </button>
                </ModalHeader>

                {!showForm && isScheduleListLoading && (
                    <Loading />
                )}
                {!isScheduleListLoading && (
                    <div className="px-6 h-full min-h-0 flex flex-col">
                        <ScrollableContent className="max-h-[calc(100vh-80px)]">
                            <Wrapper>
                                {!showForm && availableSchedules.length > 0 && (
                                    <TopBar>
                                        <SearchBox>
                                            <MdSearch className="text-gray-400" size={18} />
                                            <input
                                                type="text"
                                                placeholder="Search by report type or scheduled by..."
                                                value={searchText}
                                                onChange={(event) => setSearchText(event.target.value)}
                                                className="w-full border-none outline-none bg-transparent text-sm text-gray-800 placeholder-gray-400"
                                            />
                                        </SearchBox>
                                        <AddTopBtn type="button" onClick={handleAddNew}>
                                            Add
                                        </AddTopBtn>
                                    </TopBar>
                                )}

                                {!showForm && filteredSchedules.length > 0 && (
                                    <div className="space-y-3">
                                        {filteredSchedules.map((schedule) => {
                                            const isPaused = Number(schedule.status) === REPORT_SCHEDULE_STATUS.PAUSE;
                                            const emailsList = getEmails(schedule);
                                            const propertyIds = getPropertyIds(schedule);

                                            return (
                                                <ScheduleCard key={schedule.id} $paused={isPaused}>
                                                    <div className="flex justify-between items-start gap-3">
                                                        <div className="flex-1 min-w-0 space-y-2">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-sm font-semibold text-gray-800">
                                                                    {getReportTitle(schedule.reportType)}
                                                                </span>
                                                                {isPaused ? <Badge $yellow>Paused</Badge> : <Badge $green>Active</Badge>}
                                                            </div>
                                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                                <MdRepeat className="text-primary-color" />
                                                                <span>{getFrequencyLabel(schedule.frequency)}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                                <MdCalendarMonth size={16} />
                                                                <span>
                                                                    {schedule.scheduledDate
                                                                        ? `${moment(schedule.scheduledDate).format("DD, MMM YYYY")} at `
                                                                        : ""}
                                                                    {formatDisplayTime(schedule.sendingTime)}
                                                                </span>
                                                                {/* <span>{formatDisplayTime(schedule.sendingTime || schedule.time)}</span> */}
                                                            </div>
                                                            <div className="text-sm text-gray-600 break-words">
                                                                <span className="font-medium">Emails: </span>
                                                                {emailsList.length ? emailsList.join(", ") : "-"}
                                                            </div>

                                                            {/* UPDATED PROPERTY DISPLAY */}
                                                            <div className="text-sm text-gray-600 flex items-center gap-1">
                                                                <span className="font-medium">Properties: </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setViewPropertiesAction(schedule)}
                                                                    className="text-primary-color font-medium hover:underline focus:outline-none"
                                                                >
                                                                    {propertyIds.length} selected
                                                                </button>
                                                            </div>

                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => openConfirmation("delete", schedule)}
                                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                                            title="Delete"
                                                            aria-label={`Delete ${getReportTitle(schedule.reportType)} schedule`}
                                                        >
                                                            <MdDeleteOutline size={18} />
                                                        </button>
                                                    </div>
                                                    <div className="flex justify-between items-centergap-2 mt-4 pt-3 border-t border-gray-200">
                                                        <div className="flex gap-2">
                                                            <CardBtn type="button" onClick={() => handleEdit(schedule)}>Edit</CardBtn>
                                                            <CardBtn type="button" onClick={() => openConfirmation("toggle", schedule)}>
                                                                {isPaused ? "Resume" : "Pause"}
                                                            </CardBtn>
                                                        </div>
                                                        {schedule?.scheduledByName && (
                                                            <div className="flex items-center gap-2">
                                                                <p className="text-sm text-gray-600">
                                                                    <span className="font-medium">Scheduled by: </span> {schedule.scheduledByName?.length > 20
                                                                        ? `${schedule.scheduledByName.slice(0, 20)}...`
                                                                        : schedule.scheduledByName || "-"}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </ScheduleCard>
                                            );
                                        })}
                                    </div>
                                )}

                                {!showForm && !isScheduleListLoading && filteredSchedules.length === 0 && (
                                    <EmptyCard>
                                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 border border-blue-100 mb-5">
                                            <FaRegCalendarAlt className="text-primary-color" size={28} />
                                        </div>

                                        <h3 className="text-base font-semibold text-gray-800 mb-1">
                                            {search ? "No matching reports" : "No scheduled reports yet"}
                                        </h3>

                                        <p className="text-sm text-gray-500 max-w-sm leading-6 mb-6">
                                            {search
                                                ? "Try a different report name or adjust your search."
                                                : "Automate report delivery by scheduling reports to be sent to your inbox."}
                                        </p>

                                        {!search && (
                                            <Button
                                                type="button"
                                                onClick={handleAddNew}
                                                className="shadow-sm hover:shadow-md"
                                            >
                                                <MdAdd size={19} />
                                                Create Schedule
                                            </Button>
                                        )}
                                    </EmptyCard>
                                )}
                            </Wrapper>
                        </ScrollableContent>
                    </div>
                )}
            </Container>

            <SideModal isOpen={showForm} onClose={resetForm} level="high" width="11/12">
                <AddEditScheduledReport
                    key={editingScheduleId != null ? `edit-${editingScheduleId}` : "add"}
                    formikRef={formikRef}
                    // reportTypes={REPORT_TYPES}
                    reportTypes={reportTypes}
                    frequencyOptions={FREQUENCY_OPTIONS}
                    propertyOptions={filteredProperties}
                    initialValues={formInitialValues}
                    onSubmit={handleSubmit}
                    onCancel={resetForm}
                    isEditing={isEditing}
                    isSubmitting={isSaving}
                    handleClose={resetForm}
                />
            </SideModal>

            {/* Confirmation Modal (Pause/Resume/Delete) */}
            {confirmAction && (
                <Model
                    width="w-full max-w-md"
                    setOpenModel={(open) => { if (!open) closeConfirmation(); }}
                    title={confirmTitle}
                >
                    <WarningText>{confirmMessage}</WarningText>
                    <div className="w-full flex justify-between space-x-6 my-4">
                        <SideCancelBtn type="button" onClick={closeConfirmation} disabled={isStatusSaving}>
                            Cancel
                        </SideCancelBtn>
                        {isDeleteAction ? (
                            <DeleteBtn
                                type="button"
                                onClick={confirmAndSubmit}
                                disabled={isStatusSaving}
                                className="border border-red-500 bg-white text-red-500 hover:bg-red-500 hover:text-white disabled:opacity-60"
                            >
                                {confirmButtonText}
                            </DeleteBtn>
                        ) : (
                            <SubmitBtn
                                type="button"
                                onClick={confirmAndSubmit}
                                disabled={isStatusSaving}
                                className="disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {confirmButtonText}
                            </SubmitBtn>
                        )}
                    </div>
                </Model>
            )}

            {/* View Properties Modal */}
            {viewPropertiesAction && (
                <Model
                    width="w-full max-w-md"
                    setOpenModel={(open) => { if (!open) setViewPropertiesAction(null); }}
                    title="Selected Properties"
                >
                    <PropertyListWrapper>
                        {getSelectedPropertyNames(viewPropertiesAction).map((name, index) => (
                            <PropertyItem key={index}>
                                <span className="w-2 h-2 rounded-full bg-primary-color shrink-0 items-center" />
                                <span className="text-sm text-gray-700 break-words items-center">{name}</span>
                            </PropertyItem>
                        ))}
                    </PropertyListWrapper>
                </Model>
            )}
        </>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const TopBar = tw.div`flex items-center gap-4`;
const SearchBox = tw.div`
  flex items-center gap-1 flex-1 h-11 px-3 rounded-lg
  border border-gray-200 bg-white focus-within:border-primary-color transition-colors
`;
const AddTopBtn = tw.button`${(p) =>
    p.$active
        ? "bg-gray-100 text-white"
        : ""} border whitespace-nowrap border-primary-color flex space-x-1.5 justify-center items-center text-primary-color rounded-md px-6 cursor-pointer bg-white hover:bg-hover-color hover:text-white h-full py-2.5 text-sm`;

const EmptyCard = tw.div`
  flex flex-col items-center justify-center flex-1 min-h-[60vh]
  py-10 px-4 text-center
`;
const ScheduleCard = tw.div`
  p-4 rounded-lg border border-gray-200 bg-white
  ${(props) => (props.$paused ? "opacity-70" : "")}
`;
const CardBtn = tw.button`
  flex items-center gap-1.5 text-xs font-medium text-gray-700
  px-3 py-1.5 rounded-md border border-gray-200
  hover:bg-gray-50 hover:border-gray-300 transition-all duration-200
`;
const Badge = tw.span`
  text-xs px-2 py-0.5 rounded-full
  ${(props) => props.$yellow
        ? "bg-yellow-100 text-yellow-700"
        : props.$green ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}
`;
const WarningText = tw.p`text-gray-500 text-base mb-4`;

// New styled components for the property list modal
const PropertyListWrapper = tw.div`flex flex-col gap-3 max-h-[50vh] overflow-y-auto pr-2`;
const PropertyItem = tw.div`flex items-center gap-3 bg-gray-50 border border-gray-100 p-3 rounded-lg`;

export default ScheduleReport;